import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { platformAPI } from '../../../services/platformAPI';
import EvidencePicker from '../tender/EvidencePicker';

const EMPTY = {
  financialYear: '',
  category: '',
  currency: 'INR',
  amount: '',
  verificationStatus: 'unverified',
  notes: ''
};

const TurnoverSection = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = () => platformAPI.listTurnover()
    .then((response) => setRows(response.data?.data?.turnovers || []))
    .catch(() => setRows([]));

  useEffect(() => { load(); }, []);

  const setField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const save = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    try {
      const body = {
        ...form,
        amount: form.amount === '' ? '' : Number(form.amount),
        supportingDocumentId: documents[0]?.documentId || null
      };
      if (form._id) await platformAPI.updateTurnover(form._id, body);
      else await platformAPI.saveTurnover(body);
      setForm(EMPTY);
      setDocuments([]);
      setNotice('Turnover record saved. It applies only to the financial year and category entered.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this turnover record');
    }
  };

  const deactivate = async (id) => {
    setError('');
    try {
      await platformAPI.deactivateTurnover(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to mark this turnover record inactive');
    }
  };

  return (
    <section className="cust-section">
      <header className="cust-section__head">
        <span>08</span>
        <div>
          <h2>Annual turnover</h2>
          <p>One row is one financial year and one business category. A figure here is not assumed to qualify for every tender. A blank amount is not stored as zero.</p>
        </div>
      </header>
      {error ? <Alert variant="warning">{error}</Alert> : null}
      {notice ? <Alert variant="success">{notice}</Alert> : null}
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>Year</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Verification</th>
              <th>State</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._id}>
                <td>{row.financialYear}</td>
                <td>{row.category}</td>
                <td>{row.currency} {Number(row.amount).toLocaleString('en-IN')}</td>
                <td>{row.verificationStatus}</td>
                <td>{row.isActive ? 'Active' : 'Inactive'}</td>
                <td className="text-end">
                  <button type="button" className="btn btn-link btn-sm" onClick={() => {
                    setForm({ ...row, amount: row.amount });
                    setDocuments(row.supportingDocumentId ? [{ documentId: row.supportingDocumentId, name: 'Supporting document', version: 1 }] : []);
                  }}>Edit</button>
                  {row.isActive ? <button type="button" className="btn btn-link btn-sm" onClick={() => deactivate(row._id)}>Make inactive</button> : null}
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td colSpan="6">No turnover has been recorded yet.</td></tr> : null}
          </tbody>
        </table>
      </div>
      <div className="cust-fields">
        <Form.Group className="cust-field cust-field--3">
          <Form.Label>Financial year</Form.Label>
          <Form.Control value={form.financialYear} onChange={setField('financialYear')} placeholder="2024-25" />
        </Form.Group>
        <Form.Group className="cust-field cust-field--3">
          <Form.Label>Business category</Form.Label>
          <Form.Control value={form.category} onChange={setField('category')} placeholder="Highway consultancy" />
        </Form.Group>
        <Form.Group className="cust-field cust-field--2">
          <Form.Label>Currency</Form.Label>
          <Form.Control value={form.currency} onChange={setField('currency')} />
        </Form.Group>
        <Form.Group className="cust-field cust-field--2">
          <Form.Label>Amount</Form.Label>
          <Form.Control type="number" min="0" value={form.amount} onChange={setField('amount')} />
        </Form.Group>
        <Form.Group className="cust-field cust-field--2">
          <Form.Label>Verification</Form.Label>
          <Form.Select value={form.verificationStatus} onChange={setField('verificationStatus')}>
            <option value="unverified">Unverified</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </Form.Select>
        </Form.Group>
      </div>
      <EvidencePicker
        embedded={false}
        area="knowledge"
        label="Supporting document"
        documents={documents.slice(0, 1)}
        classification="restricted"
        onChange={(next) => setDocuments(next.slice(-1))}
      />
      <Button type="button" className="mt-3" onClick={save}>{form._id ? 'Update turnover' : 'Add turnover'}</Button>
    </section>
  );
};

export default TurnoverSection;
